import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Link as MuiLink,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import { CHATTER_AUDIENCES, CHATTER_SORT_OPTIONS } from '../../Chatter/constants';
import { ChatterSortOrder } from '../../Chatter/types';

import CaseAvatar from './CaseAvatar';
import { COMPOSER_TABS } from './constants';
import { CampaignCase, CaseFeedPost } from './types';

interface Props {
  caseItem: CampaignCase;
}

const FALLBACK_AUTHOR_NAME = 'Admin user';

function getLatestActivity(post: CaseFeedPost): number {
  return Math.max(
    dayjs(post.createdAt).valueOf(),
    ...post.comments.map((comment) => dayjs(comment.createdAt).valueOf()),
  );
}

function getPostAction(post: CaseFeedPost): string {
  if (post.type === 'created') {
    return 'created this case.';
  }
  if (post.type === 'update') {
    return 'updated this record.';
  }
  if (post.type === 'email') {
    return 'sent an email.';
  }
  return '';
}

export default function CaseFeedTab({ caseItem }: Props) {
  const { user } = useCurrentUser();
  const currentUserName = user?.fullName || FALLBACK_AUTHOR_NAME;

  const [posts, setPosts] = useState<Array<CaseFeedPost>>(caseItem.feed);
  const [composerTab, setComposerTab] = useState<(typeof COMPOSER_TABS)[number]['value']>('post');
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [audience, setAudience] = useState<string>(CHATTER_AUDIENCES[0]);
  const [sort, setSort] = useState<ChatterSortOrder>('activity');
  const [query, setQuery] = useState('');
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [likedPostIds, setLikedPostIds] = useState<Array<string>>([]);

  const composerPlaceholder = COMPOSER_TABS.find((tab) => tab.value === composerTab)?.placeholder;

  const visiblePosts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return posts
      .filter((post) => !needle || [post.author, post.text, post.updatedField].join(' ').toLowerCase().includes(needle))
      .sort((postA, postB) =>
        sort === 'latest'
          ? dayjs(postB.createdAt).valueOf() - dayjs(postA.createdAt).valueOf()
          : getLatestActivity(postB) - getLatestActivity(postA),
      );
  }, [posts, query, sort]);

  function handleShare() {
    const isEmail = composerTab === 'customer' || composerTab === 'vendor';
    const newPost: CaseFeedPost = {
      id: `draft-${Date.now()}`,
      type: isEmail ? 'email' : 'post',
      author: currentUserName,
      audience: isEmail ? undefined : audience,
      createdAt: new Date().toISOString(),
      text: draft.trim(),
      emailTo: isEmail ? [composerTab] : undefined,
      comments: [],
    };
    setPosts((current) => [newPost, ...current]);
    setDraft('');
    setIsComposerOpen(false);
  }

  function handleAddComment(postId: string) {
    const text = (commentDrafts[postId] ?? '').trim();
    if (!text) {
      return;
    }
    setPosts((current) =>
      current.map((post) =>
        post.id === postId
          ? {
              ...post,
              comments: [
                ...post.comments,
                { id: `comment-${Date.now()}`, author: currentUserName, createdAt: new Date().toISOString(), text },
              ],
            }
          : post,
      ),
    );
    setCommentDrafts((current) => ({ ...current, [postId]: '' }));
  }

  function toggleLike(postId: string) {
    setLikedPostIds((current) =>
      current.includes(postId) ? current.filter((id) => id !== postId) : [...current, postId],
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Tabs
        value={composerTab}
        onChange={(_, value) => setComposerTab(value)}
        aria-label="Feed composer type"
        sx={{ minHeight: 40 }}
      >
        {COMPOSER_TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} sx={{ minHeight: 40 }} />
        ))}
      </Tabs>

      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={isComposerOpen ? 3 : 1}
          placeholder={composerPlaceholder}
          value={draft}
          onFocus={() => setIsComposerOpen(true)}
          onChange={(event) => setDraft(event.target.value)}
          slotProps={{ htmlInput: { 'aria-label': 'Feed message' } }}
        />
        <Button variant="contained" color="primary" disabled={!draft.trim()} onClick={handleShare}>
          Share
        </Button>
      </Stack>
      {isComposerOpen && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="body2">To</Typography>
          <Select
            size="small"
            value={audience}
            onChange={(event) => setAudience(event.target.value)}
            slotProps={{ input: { 'aria-label': 'Post audience' } }}
          >
            {CHATTER_AUDIENCES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ my: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Sort by:</Typography>
          <Select
            size="small"
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            slotProps={{ input: { 'aria-label': 'Sort case feed' } }}
          >
            {CHATTER_SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            type="search"
            placeholder="Search this feed..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            slotProps={{
              htmlInput: { 'aria-label': 'Search this feed' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <IconButton
            aria-label="Refresh feed"
            onClick={() => setPosts((current) => [...current])}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {visiblePosts.length === 0 && (
        <Typography color="text.secondary">
          {query ? 'No posts match that search.' : 'No posts yet. Share an update to start the conversation.'}
        </Typography>
      )}

      <Stack spacing={1.5}>
        {visiblePosts.map((post) => (
          <Paper key={post.id} variant="outlined">
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 1.5 }}>
              <CaseAvatar name={post.author} />
              <Box flex={1} minWidth={0}>
                <Typography variant="body2">
                  <MuiLink component="span" underline="hover">
                    {post.author}
                  </MuiLink>
                  {post.audience && ` to ${post.audience}`} {getPostAction(post)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(post.createdAt).fromNow()}
                </Typography>
              </Box>
              <IconButton size="small" aria-label="Post actions" disabled>
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Stack>

            {post.type === 'update' && (
              <Box sx={{ mx: 1.5, mb: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {post.updatedField}
                </Typography>
                <Typography variant="body2" sx={{ pl: 5 }}>
                  A blank value to{' '}
                  <MuiLink component="span" underline="hover">
                    {post.updatedValue}
                  </MuiLink>
                </Typography>
              </Box>
            )}
            {post.type === 'created' && (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mx: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: 'secondary.main',
                    color: 'common.white',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <ChatBubbleOutlineIcon fontSize="small" />
                </Box>
                <Typography variant="body2">
                  <MuiLink component="span" underline="hover">
                    {caseItem.number}
                  </MuiLink>
                </Typography>
              </Stack>
            )}
            {post.type === 'email' && post.emailTo && (
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ px: 1.5, pb: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  To {post.emailTo.join(', ')}
                </Typography>
              </Stack>
            )}
            {post.text && (
              <Typography variant="body2" sx={{ px: 1.5, pb: 1.5, whiteSpace: 'pre-wrap' }}>
                {post.text}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ px: 1 }}>
              <Button
                size="small"
                color={likedPostIds.includes(post.id) ? 'primary' : 'inherit'}
                startIcon={<ThumbUpOutlinedIcon fontSize="small" />}
                onClick={() => toggleLike(post.id)}
                sx={{ textTransform: 'none' }}
              >
                Like
              </Button>
              <Button
                size="small"
                color="inherit"
                startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
                sx={{ textTransform: 'none' }}
              >
                Comment
              </Button>
            </Stack>

            <Box sx={{ bgcolor: 'action.hover', p: 1.5, mt: 0.5 }}>
              <Stack spacing={1.5}>
                {post.comments.map((comment) => (
                  <Stack key={comment.id} direction="row" spacing={1.5}>
                    <CaseAvatar name={comment.author} size={28} />
                    <Box>
                      <Typography variant="body2">
                        <MuiLink component="span" underline="hover">
                          {comment.author}
                        </MuiLink>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(comment.createdAt).fromNow()}
                      </Typography>
                      <Typography variant="body2">{comment.text}</Typography>
                    </Box>
                  </Stack>
                ))}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CaseAvatar name={currentUserName} size={28} />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Write a comment..."
                    value={commentDrafts[post.id] ?? ''}
                    onChange={(event) => setCommentDrafts((current) => ({ ...current, [post.id]: event.target.value }))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        handleAddComment(post.id);
                      }
                    }}
                    slotProps={{ htmlInput: { 'aria-label': 'Write a comment' } }}
                    sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                  />
                </Stack>
              </Stack>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
}
